import { useState, useEffect, useCallback } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import { useAuth } from '../contexts/AuthContext';
import { VEDIC_DIMENSIONS } from '../lib/vedicDimensions';
import { SAMPRADAYA_OPTIONS } from '../lib/sampradaya';
import type { SevaGig, SevaBooking } from '../lib/types';
import GigCard from './seva/GigCard';
import ServiceFilterBar from './seva/ServiceFilterBar';
import type { SevaFilter } from './seva/ServiceFilterBar';
import ProviderModal from './seva/ProviderModal';
import BookingKramaTracker from './seva/BookingKramaTracker';
import ShareableHeading from './ShareableHeading';
import '../styles/SevaGigs.css';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/api$/, '').replace(/\/$/, '');

async function sevaApiFetch(
  path: string,
  idToken: string | null,
  method = 'GET',
  body?: unknown,
): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

const DEFAULT_FILTER: SevaFilter = { dimension: null, mode: null, minAdhikara: 0, sampradaya: null };

type Tab = 'browse' | 'my-listings' | 'my-bookings' | 'create';

export default function SevaGigs() {
  const { t } = useGlossary();
  const { user, profile } = useAuth();

  const [tab, setTab] = useState<Tab>('browse');
  const [filter, setFilter] = useState<SevaFilter>(DEFAULT_FILTER);

  const [gigs, setGigs]               = useState<SevaGig[]>([]);
  const [myGigs, setMyGigs]           = useState<SevaGig[]>([]);
  const [myBookings, setMyBookings]   = useState<SevaBooking[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const [selectedGig, setSelectedGig]     = useState<{ gig: SevaGig; mode: 'intro' | 'book' } | null>(null);
  const [isBooking, setIsBooking]         = useState(false);
  const [bookingError, setBookingError]   = useState<string | null>(null);

  const [reviewTarget, setReviewTarget]   = useState<SevaBooking | null>(null);
  const [reviewForm, setReviewForm]       = useState({ hridaya: 5, vak: 5, jnana: 5, karma: 5, phala_d: 5, narrative: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Create gig form state
  const [createForm, setCreateForm] = useState({
    title: '', description: '', dimension: VEDIC_DIMENSIONS[0].id,
    rate_inr: 500, mode: 'remote', portfolio_url: '', adhikara_required: 0,
    sampradaya: '',
  });
  const [creating, setCreating]     = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try { return await user.getIdToken(); } catch { return null; }
  }, [user]);

  // Load browse gigs
  const loadGigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filter.dimension) params.set('dimension', filter.dimension);
      if (filter.mode && filter.mode !== 'either') params.set('mode', filter.mode);
      if (filter.minAdhikara) params.set('min_adhikara', String(filter.minAdhikara));
      if (filter.sampradaya) params.set('sampradaya', filter.sampradaya);
      const res = await sevaApiFetch(`/api/seva-gigs?${params.toString()}`, null);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGigs(data.gigs ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('seva.gigs.error.load'));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  // Load provider's own gigs
  const loadMyGigs = useCallback(async () => {
    const token = await getIdToken();
    if (!token) return;
    try {
      const res = await sevaApiFetch('/api/seva-gigs/my', token);
      if (!res.ok) return;
      const data = await res.json();
      setMyGigs(data.gigs ?? []);
    } catch { /* non-fatal */ }
  }, [getIdToken]);

  // Load consumer's bookings
  const loadMyBookings = useCallback(async () => {
    const token = await getIdToken();
    if (!token) return;
    try {
      const res = await sevaApiFetch('/api/seva-gigs/my-bookings/consumer', token);
      if (!res.ok) return;
      const data = await res.json();
      setMyBookings(data.bookings ?? []);
    } catch { /* non-fatal */ }
  }, [getIdToken]);

  useEffect(() => { if (tab === 'browse') loadGigs(); }, [tab, loadGigs]);
  useEffect(() => { if (tab === 'my-listings') loadMyGigs(); }, [tab, loadMyGigs]);
  useEffect(() => { if (tab === 'my-bookings') loadMyBookings(); }, [tab, loadMyBookings]);

  const handleBook = async (gig: SevaGig, brief: string, locationOptIn: boolean, introRequested = false) => {
    const token = await getIdToken();
    if (!token) { setBookingError(t('seva.gigs.book.error.auth')); return; }
    setIsBooking(true);
    setBookingError(null);
    try {
      const res = await sevaApiFetch(`/api/seva-gigs/${gig.id}/book`, token, 'POST', {
        gig_id: gig.id, brief, location_opt_in: locationOptIn, intro_requested: introRequested,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? `HTTP ${res.status}`);
      // Emit Sahacharya event (browser-local coordination only — not authoritative)
      window.dispatchEvent(new CustomEvent('sahacharya:booking-created', {
        detail: { gigId: gig.id, dimension: gig.dimension, phase: data.booking?.krama_phase, introRequested },
      }));
      setSelectedGig(null);
      setTab('my-bookings');
    } catch (e) {
      setBookingError(e instanceof Error ? e.message : t('seva.gigs.book.error.generic'));
    } finally {
      setIsBooking(false);
    }
  };

  const handleAdvanceBooking = async (booking: SevaBooking, nextPhase: string) => {
    const token = await getIdToken();
    if (!token) return;
    try {
      const res = await sevaApiFetch(
        `/api/seva-gigs/bookings/${booking.id}/advance`, token, 'POST',
        { next_phase: nextPhase, notes: '' },
      );
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail ?? t('seva.gigs.advance.error'));
        return;
      }
      const data = await res.json();
      setMyBookings((prev) => prev.map((b) => b.id === booking.id ? data.booking : b));
      window.dispatchEvent(new CustomEvent('sangati:phase-advanced', {
        detail: { bookingId: booking.id, phase: nextPhase },
      }));
    } catch { /* handled by alert */ }
  };

  const handleSubmitReview = async () => {
    if (!reviewTarget) return;
    const token = await getIdToken();
    if (!token) return;
    setReviewSubmitting(true);
    try {
      const res = await sevaApiFetch(
        `/api/seva-gigs/bookings/${reviewTarget.id}/review`, token, 'POST', reviewForm,
      );
      if (!res.ok) { const d = await res.json(); alert(d.detail); return; }
      setReviewTarget(null);
      setMyBookings((prev) => prev.map((b) =>
        b.id === reviewTarget.id ? { ...b, krama_phase: 'COMPLETE', escrow_status: 'RELEASED' } : b
      ));
      window.dispatchEvent(new CustomEvent('sahacharya:review-submitted', {
        detail: { bookingId: reviewTarget.id, dimension: reviewTarget.dimension },
      }));
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleCreateGig = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = await getIdToken();
    if (!token) { setCreateError(t('seva.gigs.book.error.auth')); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await sevaApiFetch('/api/seva-gigs', token, 'POST', createForm);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? `HTTP ${res.status}`);
      setCreateSuccess(true);
      setTab('my-listings');
      await loadMyGigs();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : t('seva.gigs.create.error.generic'));
    } finally {
      setCreating(false);
    }
  };

  const canProvide = !!user && !!profile;

  return (
    <div className="seva-gigs-page">
      <ShareableHeading
        as="h1"
        className="seva-gigs-page__heading"
        id="seva-gigs"
      >
        🤝 {t('seva.gigs.title')}
      </ShareableHeading>
      <p className="seva-gigs-page__subtitle">{t('seva.gigs.subtitle')}</p>

      {/* Tab bar */}
      <div role="tablist" className="seva-tabs" aria-label={t('seva.gigs.tabs.label')}>
        <button className={`seva-tab ${tab === 'browse' ? 'seva-tab--active' : ''}`} onClick={() => setTab('browse')}>
          🔍 {t('seva.gigs.tab.browse')}
        </button>
        {canProvide && (
          <button className={`seva-tab ${tab === 'my-listings' ? 'seva-tab--active' : ''}`} onClick={() => setTab('my-listings')}>
            📋 {t('seva.gigs.tab.my_listings')}
          </button>
        )}
        {canProvide && (
          <button className={`seva-tab ${tab === 'my-bookings' ? 'seva-tab--active' : ''}`} onClick={() => setTab('my-bookings')}>
            📦 {t('seva.gigs.tab.my_bookings')}
          </button>
        )}
        {canProvide && (
          <button className={`seva-tab ${tab === 'create' ? 'seva-tab--active' : ''}`} onClick={() => setTab('create')}>
            ＋ {t('seva.gigs.tab.create')}
          </button>
        )}
      </div>

      {/* ── BROWSE ── */}
      {tab === 'browse' && (
        <div className="seva-gigs-page__browse">
          <ServiceFilterBar filter={filter} onChange={(f) => { setFilter(f); }} />
          {loading && <p className="seva-gigs-page__loading">{t('seva.gigs.loading')}</p>}
          {error && <p className="seva-gigs-page__error" role="alert">{error}</p>}
          {!loading && gigs.length === 0 && (
            <div className="seva-gigs-page__empty">
              <p>{t('seva.gigs.empty')}</p>
            </div>
          )}
          <div className="seva-gig-grid">
            {gigs.map((gig) => (
              <GigCard
                key={gig.id}
                gig={gig}
                onBook={user
                  ? (g) => setSelectedGig({ gig: g, mode: 'book' })
                  : () => alert(t('seva.gigs.book.error.auth'))}
                onMeetFirst={user
                  ? (g) => setSelectedGig({ gig: g, mode: 'intro' })
                  : () => alert(t('seva.gigs.book.error.auth'))}
                onViewProvider={(g) => setSelectedGig({ gig: g, mode: 'book' })}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── MY LISTINGS ── */}
      {tab === 'my-listings' && (
        <div className="seva-gigs-page__my-listings">
          {myGigs.length === 0 ? (
            <div className="seva-gigs-page__empty">
              <p>{t('seva.gigs.my_listings.empty')}</p>
              <button className="seva-btn seva-btn--primary" onClick={() => setTab('create')}>
                ＋ {t('seva.gigs.tab.create')}
              </button>
            </div>
          ) : (
            <div className="seva-gig-grid">
              {myGigs.map((gig) => (
                <article key={gig.id} className="seva-gig-card seva-gig-card--mine">
                  <header className="seva-gig-card__header">
                    <span>{VEDIC_DIMENSIONS.find((d) => d.id === gig.dimension)?.icon ?? '◎'}</span>
                    <h3 className="seva-gig-card__title">{gig.title}</h3>
                  </header>
                  <p className="seva-gig-card__desc">{gig.description}</p>
                  <span className={`seva-status-badge seva-status-badge--${gig.status.toLowerCase()}`}>
                    {t(`seva.gigs.status.${gig.status.toLowerCase()}`)}
                  </span>
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── MY BOOKINGS ── */}
      {tab === 'my-bookings' && (
        <div className="seva-gigs-page__my-bookings">
          {myBookings.length === 0 ? (
            <p className="seva-gigs-page__empty">{t('seva.gigs.my_bookings.empty')}</p>
          ) : (
            <div className="seva-bookings-list">
              {myBookings.map((booking) => (
                <div key={booking.id} className="seva-booking-item">
                  <BookingKramaTracker
                    booking={booking}
                    onSubmitReview={
                      booking.krama_phase === 'REVIEW'
                        ? () => setReviewTarget(booking)
                        : undefined
                    }
                  />
                  {booking.krama_phase === 'EXECUTION' && (
                    <button
                      className="seva-btn seva-btn--primary"
                      onClick={() => handleAdvanceBooking(booking, 'REVIEW')}
                    >
                      {t('seva.gigs.advance.to_review')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CREATE GIG ── */}
      {tab === 'create' && (
        <div className="seva-gigs-page__create">
          <h2 className="seva-gigs-page__section-title">{t('seva.gigs.create.title')}</h2>
          <p className="seva-gigs-page__create-note">{t('seva.gigs.create.note')}</p>
          {createSuccess && (
            <p className="seva-gigs-page__success">{t('seva.gigs.create.success')}</p>
          )}
          <form className="seva-create-form" onSubmit={handleCreateGig}>
            <label className="seva-form-label">
              {t('seva.gigs.create.title_field')}
              <input
                type="text"
                className="seva-form-input"
                required
                value={createForm.title}
                onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
              />
            </label>

            <label className="seva-form-label">
              {t('seva.gigs.create.description_field')}
              <textarea
                className="seva-form-textarea"
                required
                rows={4}
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>

            <label className="seva-form-label">
              {t('seva.gigs.create.dimension_field')}
              <select
                className="seva-form-select"
                value={createForm.dimension}
                onChange={(e) => setCreateForm((f) => ({ ...f, dimension: e.target.value }))}
              >
                {VEDIC_DIMENSIONS.map((d) => (
                  <option key={d.id} value={d.id}>{d.icon} {d.label}</option>
                ))}
              </select>
            </label>

            <label className="seva-form-label">
              {t('seva.gigs.create.rate_field')} (₹)
              <input
                type="number"
                className="seva-form-input"
                min={1}
                required
                value={createForm.rate_inr}
                onChange={(e) => setCreateForm((f) => ({ ...f, rate_inr: Number(e.target.value) }))}
              />
            </label>

            <fieldset className="seva-form-fieldset">
              <legend className="seva-form-label">{t('seva.gigs.create.mode_field')}</legend>
              {(['remote', 'home', 'either'] as const).map((m) => (
                <label key={m} className="seva-form-radio-label">
                  <input
                    type="radio"
                    name="seva-mode"
                    value={m}
                    checked={createForm.mode === m}
                    onChange={() => setCreateForm((f) => ({ ...f, mode: m }))}
                  />
                  {m === 'home' ? `🏠 ${t('seva.gigs.mode.home')}`
                    : m === 'remote' ? `💻 ${t('seva.gigs.mode.remote')}`
                    : `🔄 ${t('seva.gigs.mode.either')}`}
                </label>
              ))}
            </fieldset>

            <label className="seva-form-label">
              {t('seva.gigs.create.sampradaya_field')}
              <select
                className="seva-form-select"
                value={createForm.sampradaya}
                onChange={(e) => setCreateForm((f) => ({ ...f, sampradaya: e.target.value }))}
              >
                <option value="">{t('seva.gigs.sampradaya.none')}</option>
                {SAMPRADAYA_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>{s.icon} {t(s.glossaryKey)}</option>
                ))}
              </select>
            </label>

            <label className="seva-form-label">
              {t('seva.gigs.create.portfolio_field')}
              <input
                type="url"
                className="seva-form-input"
                value={createForm.portfolio_url}
                onChange={(e) => setCreateForm((f) => ({ ...f, portfolio_url: e.target.value }))}
              />
            </label>

            {createError && <p className="seva-form-error" role="alert">{createError}</p>}

            <p className="seva-form-note seva-form-note--raja">
              ⚖️ {t('seva.gigs.create.raja_review_note')}
            </p>

            <button type="submit" className="seva-btn seva-btn--primary" disabled={creating}>
              {creating ? t('seva.gigs.create.submitting') : t('seva.gigs.create.submit')}
            </button>
          </form>
        </div>
      )}

      {/* ── PROVIDER / BOOK MODAL ── */}
      {selectedGig && (
        <ProviderModal
          gig={selectedGig.gig}
          mode={selectedGig.mode}
          onClose={() => { setSelectedGig(null); setBookingError(null); }}
          onBook={handleBook}
          isBooking={isBooking}
          error={bookingError}
        />
      )}

      {/* ── REVIEW MODAL ── */}
      {reviewTarget && (
        <div className="seva-modal-backdrop" role="dialog" aria-modal="true">
          <div className="seva-modal seva-modal--review">
            <header className="seva-modal__header">
              <h2>{t('seva.gigs.review.title')}</h2>
              <button className="seva-modal__close" onClick={() => setReviewTarget(null)}>✕</button>
            </header>
            <div className="seva-modal__body">
              <p className="seva-modal__desc">{t('seva.gigs.review.subtitle')}</p>
              {(['hridaya', 'vak', 'jnana', 'karma', 'phala_d'] as const).map((key) => (
                <label key={key} className="seva-form-label">
                  {t(`seva.gigs.review.sub_score.${key}`)} ({reviewForm[key]})
                  <input
                    type="range" min={0} max={9}
                    value={reviewForm[key]}
                    onChange={(e) => setReviewForm((f) => ({ ...f, [key]: Number(e.target.value) }))}
                    className="seva-filter-bar__slider"
                  />
                </label>
              ))}
              <label className="seva-form-label">
                {t('seva.gigs.review.narrative')}
                <textarea
                  className="seva-form-textarea"
                  rows={3}
                  value={reviewForm.narrative}
                  onChange={(e) => setReviewForm((f) => ({ ...f, narrative: e.target.value }))}
                />
              </label>
              <div className="seva-modal__form-actions">
                <button className="seva-btn seva-btn--secondary" onClick={() => setReviewTarget(null)}>
                  {t('common.cancel')}
                </button>
                <button className="seva-btn seva-btn--primary" onClick={handleSubmitReview} disabled={reviewSubmitting}>
                  {reviewSubmitting ? t('seva.gigs.review.submitting') : t('seva.gigs.review.submit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
